import React, { useMemo, useState } from 'react'

function normalizeValueByType(type, raw) {
  if (raw === null || raw === undefined) {
    return type === 'multi-select' || type === 'checkbox' ? [] : ''
  }
  if (type === 'multi-select' || type === 'checkbox') {
    return Array.isArray(raw) ? raw : []
  }
  if (type === 'number') {
    return raw === '' ? '' : String(raw)
  }
  if (typeof raw === 'object') {
    try { return JSON.stringify(raw) } catch { return String(raw) }
  }
  return String(raw)
}

export function normalizeSurveyAnswerMap(answerRows = []) {
  const map = {}
  for (const row of Array.isArray(answerRows) ? answerRows : []) {
    map[row.questionKey] = row.answer
  }
  return map
}

export function listSurveyQuestions(schema) {
  const sections = Array.isArray(schema?.sections) ? schema.sections : []
  const out = []
  sections.forEach(section => {
    const questions = Array.isArray(section?.questions) ? section.questions : []
    questions.forEach(question => {
      if (question?.key) {
        out.push({ sectionKey: section?.key || 'default', question })
      }
    })
  })
  return out
}

export function computeSurveyProgress(schema, answers) {
  const questions = listSurveyQuestions(schema)
  if (questions.length === 0) return 0
  const answeredCount = questions.filter(({ question }) => {
    const value = answers?.[question.key]
    if (Array.isArray(value)) return value.length > 0
    return value !== undefined && value !== null && String(value).trim() !== ''
  }).length
  return Math.round((answeredCount / questions.length) * 100)
}

export function buildSurveyAnswerInputs(schema, answers) {
  return listSurveyQuestions(schema)
    .map(({ sectionKey, question }) => {
      const value = answers?.[question.key]
      if (Array.isArray(value) && value.length === 0) return null
      if (!Array.isArray(value) && (value === undefined || value === null || String(value).trim() === '')) return null
      return {
        sectionKey,
        blockKey: question.blockKey || null,
        questionKey: question.key,
        questionType: question.type || 'text',
        answer: value,
        agentOnly: question.agentOnly === true,
      }
    })
    .filter(Boolean)
}

function getFieldType(question) {
  const type = String(question?.type || 'text').toLowerCase()
  if (type === 'single-select') return 'radio'
  if (type === 'long-text') return 'textarea'
  if (type === 'multi-select') return 'checkbox'
  return type
}

function renderOptionLabel(option) {
  if (typeof option === 'string') return option
  return option?.label || option?.value || '-'
}

function renderOptionValue(option) {
  if (typeof option === 'string') return option
  return option?.value || option?.label || ''
}

function inferBirthDateFromResidentNumber(raw) {
  const digits = String(raw || '').replace(/[^0-9]/g, '')
  if (digits.length < 6) return null

  const yy = Number(digits.slice(0, 2))
  const mm = Number(digits.slice(2, 4))
  const dd = Number(digits.slice(4, 6))
  if (!Number.isInteger(yy) || !Number.isInteger(mm) || !Number.isInteger(dd)) return null
  if (mm < 1 || mm > 12 || dd < 1 || dd > 31) return null

  let yearPrefix = null
  if (digits.length >= 7) {
    const centuryCode = digits[6]
    if (['1', '2', '5', '6'].includes(centuryCode)) yearPrefix = 1900
    if (['3', '4', '7', '8'].includes(centuryCode)) yearPrefix = 2000
    if (['9', '0'].includes(centuryCode)) yearPrefix = 1800
  }
  if (yearPrefix == null) {
    const currentTwoDigits = new Date().getFullYear() % 100
    yearPrefix = yy <= currentTwoDigits ? 2000 : 1900
  }

  const yyyy = yearPrefix + yy
  const normalized = `${yyyy}-${String(mm).padStart(2, '0')}-${String(dd).padStart(2, '0')}`
  const date = new Date(`${normalized}T00:00:00`)
  if (Number.isNaN(date.getTime())) return null
  return normalized
}

function isResidentRegistrationQuestion(question) {
  return String(question?.key || '') === 'residentRegistrationNumber'
}

/** 종합상담 등 설문에서 연락처·생년월일 필드 식별 */
function isPhoneLikeQuestion(question) {
  const k = String(question?.key || '').toLowerCase()
  if (['tel', 'phone', 'mobile', 'customerphone', 'contactphone'].some((x) => k === x || k.includes(x))) return true
  if (getFieldType(question) === 'tel') return true
  const label = String(question?.label || '')
  return /연락처|휴대폰|핸드폰|휴대전화|전화번호/.test(label)
}

function isBirthDateLikeQuestion(question) {
  const k = String(question?.key || '').toLowerCase()
  if (k.includes('birth')) return true
  const label = String(question?.label || '')
  return /생년월일|생일/.test(label)
}

function formatPhoneKoSurvey(raw) {
  const d = String(raw || '').replace(/[^0-9]/g, '').slice(0, 11)
  if (!d) return ''
  if (d.length <= 3) return d
  if (d.length <= 7) return `${d.slice(0, 3)}-${d.slice(3)}`
  if (d.length <= 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`
}

function formatBirthDateTyping(raw) {
  const d = String(raw || '').replace(/[^0-9]/g, '').slice(0, 8)
  if (d.length <= 4) return d
  if (d.length <= 6) return `${d.slice(0, 4)}-${d.slice(4)}`
  return `${d.slice(0, 4)}-${d.slice(4, 6)}-${d.slice(6)}`
}

function krMobileDigitsOk(formatted) {
  const n = String(formatted || '').replace(/[^0-9]/g, '').length
  return n === 0 || n === 10 || n === 11
}

export default function CounselSurveyForm({
  schema,
  initialAnswers,
  onSave,
  onSubmit,
  saveLabel = '임시저장',
  submitLabel = '설문 제출',
  saving = false,
  submitting = false,
  saveDisabled = false,
  submitDisabled = false,
}) {
  const [answers, setAnswers] = useState(() => normalizeSurveyAnswerMap(initialAnswers))

  React.useEffect(() => {
    setAnswers(normalizeSurveyAnswerMap(initialAnswers))
  }, [initialAnswers])

  const sections = useMemo(() => (Array.isArray(schema?.sections) ? schema.sections : []), [schema])
  const progressPercent = useMemo(() => computeSurveyProgress(schema, answers), [schema, answers])

  const updateValue = (question, rawValue) => {
    let value = rawValue
    if (isPhoneLikeQuestion(question)) {
      value = formatPhoneKoSurvey(rawValue)
    } else if (isBirthDateLikeQuestion(question)) {
      value = formatBirthDateTyping(rawValue)
    }
    setAnswers(prev => {
      const next = { ...prev, [question.key]: normalizeValueByType(getFieldType(question), value) }
      if (question?.key === 'residentRegistrationNumber') {
        const inferredBirthDate = inferBirthDateFromResidentNumber(value)
        if (inferredBirthDate) {
          next.birthDate = inferredBirthDate
        }
      }
      return next
    })
  }

  const toggleCheckbox = (question, value) => {
    setAnswers(prev => {
      const current = Array.isArray(prev[question.key]) ? prev[question.key] : []
      const next = current.includes(value)
        ? current.filter(item => item !== value)
        : [...current, value]
      return { ...prev, [question.key]: next }
    })
  }

  const handleSave = async () => {
    await onSave?.(answers, progressPercent)
  }

  const handleSubmit = async () => {
    await onSubmit?.(answers, 100)
  }

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-3xl border bg-brand-navy text-white shadow-sm">
        <div className="bg-[linear-gradient(135deg,rgba(255,255,255,0.08),transparent_55%)] p-6">
          <div className="text-xs font-semibold tracking-[0.24em] text-amber-300">CUSTOMER SURVEY</div>
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold">{schema?.title || '고객 설문'}</h1>
              {schema?.description && <div className="mt-2 max-w-3xl text-sm text-white/75">{schema.description}</div>}
            </div>
            <div className="min-w-[180px] rounded-2xl border border-white/15 bg-white/10 px-4 py-3">
              <div className="flex items-center justify-between text-xs text-white/70">
                <span>작성 진행률</span>
                <span>{progressPercent}%</span>
              </div>
              <div className="mt-2 h-2 rounded-full bg-white/15">
                <div className="h-2 rounded-full bg-sky-300 transition-all" style={{ width: `${progressPercent}%` }} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {sections.map((section, sectionIndex) => (
        <section key={section.key || section.title} className="rounded-3xl border bg-white p-5 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="rounded-xl bg-brand-navy px-3 py-2 text-xs font-semibold tracking-[0.18em] text-white">
              PART {String(sectionIndex + 1).padStart(2, '0')}
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{section.title || '설문 항목'}</h2>
              {section.description && <div className="mt-1 text-sm text-slate-500">{section.description}</div>}
            </div>
          </div>
          <div className="mt-4 space-y-5">
            {(Array.isArray(section.questions) ? section.questions : []).map((question, questionIndex) => {
              const fieldType = getFieldType(question)
              const value = normalizeValueByType(fieldType, answers[question.key])
              const options = Array.isArray(question.options) ? question.options : []
              const birthLike = isBirthDateLikeQuestion(question)
              const phoneLike = isPhoneLikeQuestion(question)

              const htmlInputType = isResidentRegistrationQuestion(question)
                ? 'password'
                : birthLike
                  ? 'text'
                  : fieldType === 'long-text'
                    ? 'text'
                    : fieldType === 'date'
                      ? 'date'
                      : fieldType

              return (
                <div key={question.key} className="rounded-2xl border border-slate-200 bg-slate-50/60 p-4">
                  <div className="flex items-start gap-2">
                    <div className="mt-0.5 rounded-md bg-brand-navy px-2 py-1 text-xs font-semibold text-white">
                      Q{questionIndex + 1}
                    </div>
                    <div className="flex-1">
                      <div className="font-medium text-slate-900">
                        {question.label || question.key}
                        {question.required && <span className="ml-1 text-rose-500">*</span>}
                      </div>
                      {question.helpText && <div className="mt-1 text-xs text-slate-500">{question.helpText}</div>}
                    </div>
                  </div>

                  {(fieldType === 'text' || fieldType === 'tel' || fieldType === 'date' || fieldType === 'number') && (
                    <input
                      type={htmlInputType}
                      className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      placeholder={
                        birthLike
                          ? '1999-11-11'
                          : (question.placeholder || '')
                      }
                      autoComplete={isResidentRegistrationQuestion(question) ? 'off' : undefined}
                      inputMode={birthLike || phoneLike ? 'numeric' : undefined}
                      onChange={(e) => updateValue(question, e.target.value)}
                    />
                  )}
                  {phoneLike && value && !krMobileDigitsOk(value) && (
                    <p className="mt-1 text-xs text-amber-800">휴대폰 번호는 10~11자리 숫자로 입력해 주세요.</p>
                  )}

                  {(fieldType === 'textarea') && (
                    <textarea
                      className="mt-3 min-h-[120px] w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      placeholder={question.placeholder || ''}
                      onChange={(e) => updateValue(question, e.target.value)}
                    />
                  )}

                  {(fieldType === 'select') && (
                    <select
                      className="mt-3 w-full rounded-lg border px-3 py-2 text-sm"
                      value={value}
                      onChange={(e) => updateValue(question, e.target.value)}
                    >
                      <option value="">선택해 주세요</option>
                      {options.map(option => (
                        <option key={renderOptionValue(option)} value={renderOptionValue(option)}>
                          {renderOptionLabel(option)}
                        </option>
                      ))}
                    </select>
                  )}

                  {(fieldType === 'radio') && (
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {options.map(option => {
                        const optionValue = renderOptionValue(option)
                        const selected = value === optionValue
                        return (
                          <label
                            key={optionValue}
                            className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${selected ? 'border-brand-navy bg-sky-50 text-brand-navy ring-1 ring-brand-navy/10' : 'bg-white hover:bg-slate-50'}`}
                          >
                            <input
                              type="radio"
                              name={question.key}
                              checked={value === optionValue}
                              onChange={() => updateValue(question, optionValue)}
                            />
                            <span>{renderOptionLabel(option)}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}

                  {(fieldType === 'checkbox') && (
                    <div className="mt-3 grid gap-2 md:grid-cols-2">
                      {options.map(option => {
                        const optionValue = renderOptionValue(option)
                        const selected = Array.isArray(value) && value.includes(optionValue)
                        return (
                          <label
                            key={optionValue}
                            className={`flex cursor-pointer items-center gap-2 rounded-xl border px-3 py-2 text-sm transition ${selected ? 'border-brand-sky bg-sky-50 text-brand-navy' : 'bg-white hover:bg-slate-50'}`}
                          >
                            <input
                              type="checkbox"
                              checked={Array.isArray(value) && value.includes(optionValue)}
                              onChange={() => toggleCheckbox(question, optionValue)}
                            />
                            <span>{renderOptionLabel(option)}</span>
                          </label>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </section>
      ))}

      <div className="sticky bottom-0 z-10 rounded-2xl border bg-white/95 p-4 backdrop-blur">
        <div className="flex flex-wrap justify-end gap-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || saveDisabled}
            className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-50 disabled:opacity-60"
          >
            {saving ? '저장 중...' : saveLabel}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting || submitDisabled}
            className="rounded-lg bg-brand-navy px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
          >
            {submitting ? '제출 중...' : submitLabel}
          </button>
        </div>
      </div>
    </div>
  )
}
